/**
 * brochureVectorLuxury.js — Layout LUXURY in vector PDF (jsPDF).
 *
 * Stile: whitespace generoso, Bodoni-like (times bold), centrato.
 * Cover sobria con foto piena + cornice bianca interna + esclusiva.
 * Inner pages con generoso margine e tipografia high-end.
 */
import { A4_W, A4_H, hexToRgb, setFill, setStroke, setText, safe, getAttrs, cleanCity, loadImageAsJpegDataUrl, loadAgencyLogo, addPhotoCover, addLogoFit, shortenEmail, cityIfNotInTitle } from "./brochurePdfShared";

export async function buildCoverLuxury(pdf, data, col, showPrice = true) {
  // Foto full bleed
  // iter v28 r16 — alta risoluzione + cover-fit (no più stretch dell'hero).
  const hero = await loadImageAsJpegDataUrl(data.photos.hero, 2400);
  if (hero) await addPhotoCover(pdf, hero, 0, 0, A4_W, A4_H);
  else { setFill(pdf, col.bg); pdf.rect(0, 0, A4_W, A4_H, "F"); }

  // Vignetta scura per leggibilità testi (overlay 35% nera)
  setFill(pdf, "#000000");
  pdf.setGState(new pdf.GState({ opacity: 0.32 }));
  pdf.rect(0, 0, A4_W, A4_H, "F");
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Cornice bianca interna a 12mm dal bordo
  const fr = 12;
  setStroke(pdf, "#FAFAF7");
  pdf.setLineWidth(0.6);
  pdf.setGState(new pdf.GState({ opacity: 0.85 }));
  pdf.rect(fr, fr, A4_W - 2 * fr, A4_H - 2 * fr, "S");
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Logo + agency name CENTRATO in cima
  // iter v28 r17 — Logo 26mm → 38mm per richiesta utente ("logo troppo
  // piccolo"). Uso addLogoFit che preserva aspect ratio (no stretching su
  // logo rettangolari) e centra automaticamente nella box.
  let topY = 32;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 900, true);
    if (logo) {
      const placed = addLogoFit(pdf, logo, (A4_W - 40) / 2, topY - 19, 40, 26, { align: "center" });
      if (placed) topY += placed.h / 2 + 8;
    }
  }
  pdf.setFont("times", "italic");
  pdf.setFontSize(13);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.95 }));
  const agname = safe(data.agencyName, "");
  if (agname) {
    const w = pdf.getTextWidth(agname);
    pdf.text(agname, (A4_W - w) / 2, topY);
  }
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // ESCLUSIVA (uppercase tracked) centrato in alto SOPRA il titolo
  // iter v28r8: prima era nel mezzo della pagina staccata, ora subito sopra
  // il titolo (tra città e prezzo blocco) per essere parte integrante del
  // testo principale.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.85 }));
  const excl = "E S C L U S I V A";
  const ew = pdf.getTextWidth(excl);
  pdf.text(excl, (A4_W - ew) / 2, A4_H / 2 + 20);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Titolo principale Bodoni-style, CENTRATO, multi-line
  pdf.setFont("times", "bolditalic");
  pdf.setFontSize(26);
  setText(pdf, "#FAFAF7");
  const title = safe(data.title, "Immobile esclusivo");
  const lines = pdf.splitTextToSize(title, A4_W - 48);
  // iter v28r8: centrare verticalmente il blocco titolo/città/esclusiva
  // attorno a A4_H/2 + 30 (sotto la label esclusiva)
  let titleY = A4_H / 2 + 35;
  lines.slice(0, 3).forEach((line, i) => {
    const w = pdf.getTextWidth(line);
    pdf.text(line, (A4_W - w) / 2, titleY + i * 10);
  });

  // Città centrata (solo se non duplicata nel titolo)
  const cityShow = cityIfNotInTitle(data);
  if (cityShow) {
    const cityY = titleY + Math.min(lines.length, 3) * 10 + 6;
    pdf.setFont("times", "italic");
    pdf.setFontSize(14);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.9 }));
    const cw = pdf.getTextWidth(cityShow);
    pdf.text(cityShow, (A4_W - cw) / 2, cityY);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Prezzo centrato in basso (Bodoni grande)
  if (showPrice) {
    pdf.setFont("times", "bold");
    pdf.setFontSize(32);
    setText(pdf, "#FAFAF7");
    const price = safe(data.price, "—");
    const pw = pdf.getTextWidth(price);
    pdf.text(price, (A4_W - pw) / 2, A4_H - 38);
  }

  // mq · locali sotto al prezzo
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.85 }));
  const meta = `${data.surface || "—"} mq  ·  ${data.rooms || "—"} locali`;
  const mw = pdf.getTextWidth(meta);
  pdf.text(meta, (A4_W - mw) / 2, A4_H - 28);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}

export async function buildDescriptionLuxury(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // iter v28 r17 — Layout rifatto per richiesta utente:
  //   • Niente più header "LA PROPRIETÀ" (l'utente lo chiama "La residenza"
  //     e vuole più spazio per la descrizione).
  //   • Descrizione COMPLETA (no più troncamento a 480 char): wrapping su
  //     più paragrafi, max 38 righe a 9.5pt → ~2200 caratteri reali.
  //   • Foto in alto + descrizione sotto in colonna unica giustificata.
  //   • Attributi: pillole orizzontali compatte (truncate label, no overflow).

  // Header sobrio: titolo + città in alto, no decorazione hardcoded
  pdf.setFont("times", "italic");
  pdf.setFontSize(22);
  setText(pdf, "#1C1C1A");
  const title = safe(data.title, "Immobile esclusivo");
  const titleLines = pdf.splitTextToSize(title, A4_W - 36);
  let ty = 28;
  titleLines.slice(0, 2).forEach((line) => {
    const w = pdf.getTextWidth(line);
    pdf.text(line, (A4_W - w) / 2, ty);
    ty += 9;
  });

  // Città piccola sotto il titolo
  const cityShow = cityIfNotInTitle(data);
  if (cityShow) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    setText(pdf, col.accent);
    const t = cityShow.toUpperCase().split("").join(" ");
    const cw = pdf.getTextWidth(t);
    pdf.text(t, (A4_W - cw) / 2, ty + 1);
    ty += 6;
  }

  // Foto principale (ridotta in altezza, lascia spazio alla descrizione)
  const photoY = ty + 4;
  const photoH = 78;
  const photoW = A4_W - 36;
  const photoX = (A4_W - photoW) / 2;
  const p2 = data.photos.p2 ? await loadImageAsJpegDataUrl(data.photos.p2, 2000) : null;
  if (p2) {
    await addPhotoCover(pdf, p2, photoX, photoY, photoW, photoH);
  } else {
    setFill(pdf, "#F1F4ED");
    pdf.rect(photoX, photoY, photoW, photoH, "F");
  }

  // Descrizione COMPLETA giustificata
  const descY = photoY + photoH + 9;
  const desc = (data.description || data.subtitle || "").trim();
  if (desc) {
    pdf.setFont("times", "italic");
    pdf.setFontSize(10);
    setText(pdf, "#1C1C1A");
    pdf.setGState(new pdf.GState({ opacity: 0.88 }));
    // No più troncamento aggressivo. Calcolo righe disponibili = (spazio fino agli
    // attributi) / line-height. Max 38 righe.
    const wrapped = pdf.splitTextToSize(desc, A4_W - 36);
    const attrsTop = A4_H - 48;  // dove iniziano gli attributi
    const lineH = 4.6;
    const maxLines = Math.floor((attrsTop - descY) / lineH);
    const lines = wrapped.slice(0, Math.max(4, maxLines));
    if (wrapped.length > lines.length && lines.length > 0) {
      lines[lines.length - 1] = String(lines[lines.length - 1]).replace(/.{0,3}$/, "…");
    }
    let qy = descY;
    lines.forEach((line) => {
      pdf.text(line, 18, qy);
      qy += lineH;
    });
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Strip attributi orizzontale leggibile (truncate per non overflow)
  const attrs = getAttrs(data);
  if (attrs.length > 0) {
    const stripY = A4_H - 38;
    const slotW = (A4_W - 36) / attrs.length;
    attrs.forEach(([k, v], i) => {
      const x = 18 + i * slotW;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      setText(pdf, col.accent);
      // Truncate label se servisse
      const labText = k.length > 12 ? k.slice(0, 11) + "." : k;
      const labW = pdf.getTextWidth(labText);
      pdf.text(labText, x + slotW / 2 - labW / 2, stripY);

      pdf.setFont("times", "italic");
      pdf.setFontSize(13);
      setText(pdf, "#1C1C1A");
      const val = safe(v);
      const valTrunc = val.length > 14 ? val.slice(0, 13) + "…" : val;
      const vw = pdf.getTextWidth(valTrunc);
      pdf.text(valTrunc, x + slotW / 2 - vw / 2, stripY + 7);
    });
  }

  await drawFooter(pdf, data, "02");
}

export async function buildGalleryLuxury(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // Header centrato
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, col.accent);
  const hdr = "G A L L E R I A";
  const hw = pdf.getTextWidth(hdr);
  pdf.text(hdr, (A4_W - hw) / 2, 24);
  setStroke(pdf, col.accent);
  pdf.setLineWidth(0.4);
  pdf.line(A4_W / 2 - 15, 27, A4_W / 2 + 15, 27);

  const urls = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5].filter(Boolean);
  // iter v28 r16 — bump risoluzione 1400→2000
  const photos = await Promise.all(urls.map((u) => loadImageAsJpegDataUrl(u, 2000)));

  // 1 grande in alto + 4 thumbs sotto in griglia 2×2
  const top = 38;
  const heroH = (A4_H - top - 18) * 0.55;
  const gridY = top + heroH + 4;
  const gridH = A4_H - 18 - gridY;
  const m = 18;
  if (photos[0]) await addPhotoCover(pdf, photos[0], m, top, A4_W - 2 * m, heroH);
  const thumbW = (A4_W - 2 * m - 4) / 2;
  const thumbH = (gridH - 4) / 2;
  const positions = [[m, gridY], [m + thumbW + 4, gridY], [m, gridY + thumbH + 4], [m + thumbW + 4, gridY + thumbH + 4]];
  for (let i = 0; i < 4; i++) {
    const ph = photos[i + 1];
    if (ph) await addPhotoCover(pdf, ph, positions[i][0], positions[i][1], thumbW, thumbH);
    else { setFill(pdf, "#F1F4ED"); pdf.rect(positions[i][0], positions[i][1], thumbW, thumbH, "F"); }
  }

  drawFooter(pdf, data, "03");
}

export async function buildContactsLuxury(pdf, data, col, showProntoLabel = true) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // Cornice accent leggera
  setStroke(pdf, col.accent);
  pdf.setLineWidth(0.4);
  pdf.setGState(new pdf.GState({ opacity: 0.35 }));
  pdf.rect(14, 14, A4_W - 28, A4_H - 28, "S");
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Logo agency centrato in alto
  // iter v28r7: contatti su bg chiaro #FAFAF7 → tint black se mono
  // iter v28 r17: addLogoFit (no stretching) + bump 36 → 44mm
  let topY = 36;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 900, false);
    if (logo) {
      const placed = addLogoFit(pdf, logo, (A4_W - 50) / 2, topY, 50, 28, { align: "center" });
      if (placed) topY += placed.h + 6;
    }
  }

  pdf.setFont("times", "bolditalic");
  pdf.setFontSize(20);
  setText(pdf, "#1C1C1A");
  const agency = safe(data.agencyName, "La tua agenzia");
  const agencyLines = pdf.splitTextToSize(agency, A4_W - 48);
  agencyLines.slice(0, 2).forEach((line, i) => {
    const w = pdf.getTextWidth(line);
    pdf.text(line, (A4_W - w) / 2, topY + 8 + i * 8);
  });

  if (data.agencyWeb) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    setText(pdf, col.accent);
    pdf.setGState(new pdf.GState({ opacity: 0.8 }));
    const w = pdf.getTextWidth(data.agencyWeb.toUpperCase());
    pdf.text(data.agencyWeb.toUpperCase(), (A4_W - w) / 2, topY + 8 + agencyLines.length * 8 + 4);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Divider centrale
  setStroke(pdf, col.accent);
  pdf.setLineWidth(0.4);
  pdf.line(A4_W / 2 - 20, A4_H / 2 - 18, A4_W / 2 + 20, A4_H / 2 - 18);

  // Label agente centrato
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, col.accent);
  const lab = "A G E N T E   D I   R I F E R I M E N T O";
  const lw = pdf.getTextWidth(lab);
  pdf.text(lab, (A4_W - lw) / 2, A4_H / 2 - 8);

  // Agent photo centrato + nome + telefono + email
  const agentY = A4_H / 2 + 5;
  if (data.agentPhoto) {
    // iter v28 r16 — bump risoluzione + cover-fit (centra il volto)
    const ph = await loadImageAsJpegDataUrl(data.agentPhoto, 900);
    if (ph) {
      const r = 14;
      const cx = A4_W / 2;
      const cy = agentY + r;
      setFill(pdf, "#FAFAF7");
      pdf.circle(cx, cy, r, "F");
      const innerSize = (r - 1.5) * 2;
      await addPhotoCover(pdf, ph, cx - r + 1.5, cy - r + 1.5, innerSize, innerSize, { anchor: "top" });
      setStroke(pdf, col.accent);
      pdf.setLineWidth(0.6);
      pdf.circle(cx, cy, r, "S");
    }
  }

  pdf.setFont("times", "italic");
  pdf.setFontSize(22);
  setText(pdf, "#1C1C1A");
  const agent = safe(data.agentName, "Agente immobiliare");
  const aw = pdf.getTextWidth(agent);
  pdf.text(agent, (A4_W - aw) / 2, agentY + 38);

  // Contatti centrati 2-colonne
  const contactY = A4_H - 56;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, col.accent);
  if (data.agentPhone) {
    const phoneLabel = "T E L E F O N O";
    const tlw = pdf.getTextWidth(phoneLabel);
    pdf.text(phoneLabel, A4_W / 4 - tlw / 2, contactY);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    setText(pdf, "#1C1C1A");
    const phw = pdf.getTextWidth(data.agentPhone);
    pdf.text(data.agentPhone, A4_W / 4 - phw / 2, contactY + 7);
  }
  if (data.agentEmail) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, col.accent);
    const eml = "E M A I L";
    const elw = pdf.getTextWidth(eml);
    pdf.text(eml, (3 * A4_W) / 4 - elw / 2, contactY);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setText(pdf, "#1C1C1A");
    const emailDisplay = shortenEmail(data.agentEmail, 28);
    const ew = pdf.getTextWidth(emailDisplay);
    pdf.text(emailDisplay, (3 * A4_W) / 4 - ew / 2, contactY + 7);
  }

  if (showProntoLabel) {
    // iter v28 r17 — Watermark del social (mark-symbol.png) al posto del
    // testo "P R O N T O I M M O . I T". L'utente vuole coerenza visiva
    // tra brochure e post social.
    try {
      const wmRes = await fetch("/logo/mark-symbol.png");
      if (wmRes.ok) {
        const wmBlob = await wmRes.blob();
        const wmDataUrl = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(wmBlob);
        });
        if (wmDataUrl) {
          pdf.setGState(new pdf.GState({ opacity: 0.55 }));
          // 9mm di altezza, centrato in basso
          const wmH = 9;
          const wmW = 9; // mark-symbol è quadrato 1:1
          pdf.addImage(wmDataUrl, "PNG", (A4_W - wmW) / 2, A4_H - 18, wmW, wmH);
          pdf.setGState(new pdf.GState({ opacity: 1 }));
        }
      }
    } catch (_) {
      // Fallback testuale se il PNG non è raggiungibile
      pdf.setGState(new pdf.GState({ opacity: 0.35 }));
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6.5);
      setText(pdf, "#1C1C1A");
      const wm = "P R O N T O I M M O . I T";
      const wmW = pdf.getTextWidth(wm);
      pdf.text(wm, (A4_W - wmW) / 2, A4_H - 14);
      pdf.setGState(new pdf.GState({ opacity: 1 }));
    }
  }
}

function drawFooter(pdf, data, num) {
  setStroke(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.14 }));
  pdf.setLineWidth(0.2);
  pdf.line(18, A4_H - 18, A4_W - 18, A4_H - 18);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.6 }));
  const city = cleanCity(data.city);
  const label = [data.agencyName, city].filter(Boolean).join(" · ").toUpperCase();
  const lw = pdf.getTextWidth(label);
  pdf.text(label, (A4_W - lw) / 2, A4_H - 12);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}
